import { useEffect, useState } from "react";
import { decodeHtmlEntities } from "constant/htmlEntities";
import VideoCreatorButtonSection from "./shared/VideoCreatorButtonSection";
import { useNavigate, useParams } from "react-router-dom";
import LazyLoadImg from "components/LazyLoadImg";
import CreatorHeaderSkeleton from "components/skeleton/CreatorHeaderSkeleton";
import CreatorService from "services/creators/CreatorService";
import BackButton from "components/shared/BackButton";
import AnimatedShareButton from "./shared/AnimatedShareButton";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import verify from "Assets/Images/verify.png";
import LoginModel from "../PayPerView/shared/LoginModal";
import SubscribeCreator from "./shared/SubscribeCreator";
import FollowFollowingText from "pages/newProfilePage/shared/FollowFollowingText";
import BackdropImg from "../../Assets/Images/backdrop.png";
import { FaCheckCircle, FaCrown } from "react-icons/fa";
import { RouteName } from "constant/routeNames";

interface PackageResponse {
  id: number;
  entitlement_id: string;
  title: string;
  price: string;
  thumbnail: string;
  created_at: string;
  description: string;
  is_subscribed: boolean;
  role_id: number;
  color?: string;
  slug: string;
}

function VideoCreator({
  handleClick,
  isActive,
  setIsActive,
  setPlayerIsActive,
  anyPlayerActive,
}: any) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const BaseUrl = window.location.origin;
  const currentUrl = BaseUrl + window.location.pathname;
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const [creatorData, setCreatorData] = useState<any>();
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const FE_URL = process.env.REACT_APP_FRONTEND_URL;

  const isPlusUser = loggedInUser?.is_subscribed;

  const packageObject: PackageResponse = {
    id: creatorData?.general_info?.service_id,
    entitlement_id: creatorData?.general_info?.entitlement_id,
    title: creatorData?.general_info?.name,
    price: creatorData?.general_info?.price,
    thumbnail: creatorData?.general_info?.thumbnail_url,
    created_at: creatorData?.general_info?.created_at || "",
    description: creatorData?.general_info?.description,
    is_subscribed: creatorData?.general_info?.is_subscribed,
    role_id: creatorData?.general_info?.plan_id,
    slug: creatorData?.general_info?.slug,
  };

  const isPremiumContent = creatorData?.general_info?.plan_id === 2;

  useEffect(() => {
    if (!anyPlayerActive) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  const stripHtmlTags = (str: any) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = str;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const cleanDescription = (description: any) => {
    if (!description) {
      return "";
    }
    return stripHtmlTags(decodeHtmlEntities(description));
  };

  useEffect(() => {
    setIsLoading(true);
    const CleanedSlug = slug?.slice(1);
    if (slug) {
      CreatorService.getCreatorBySlug(CleanedSlug).then((res) => {
        setIsLoading(false);
        setCreatorData(res);
        console.log(creatorData?.general_info?.type);
      });
    }
  }, [slug]);

  const rawDescription = creatorData?.general_info?.description || "";

  const fullDescription = cleanDescription(rawDescription);

  const closeLoginModal = () => {
    setShowLoginModal(false);
  };

  const isCreator = creatorData?.general_info?.type?.some(
    (item: any) => item.name === "Creator",
  );

  return (
    <>
      <Helmet>
        <title>
          {creatorData?.general_info?.name
            ? `${creatorData?.general_info?.name} - AYOZAT`
            : "AYOZAT"}
        </title>
        <meta name="title" content={creatorData?.general_info?.title} />
        <meta
          name="description"
          content={cleanDescription(rawDescription)}
        />
        <link
          rel="canonical"
          href={`${FE_URL}/@${creatorData?.general_info?.username}`}
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={creatorData?.general_info?.title} />
        <meta
          name="twitter:description"
          content={cleanDescription(creatorData?.general_info?.description)}
        />
        <meta
          name="twitter:image"
          content={creatorData?.general_info?.thumbnail_url}
        />
        <meta
          name="twitter:url"
          content={`${FE_URL}/@${creatorData?.general_info?.username}`}
        />

        <meta property="og:title" content={creatorData?.general_info?.name} />
        <meta
          property="og:description"
          content={cleanDescription(creatorData?.general_info?.description)}
        />
        <meta
          property="og:image"
          content={creatorData?.general_info?.thumbnail_url}
        />
        <meta
          property="og:url"
          content={`${FE_URL}/@${creatorData?.general_info?.username}`}
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <div>
        <div className="relative w-full max-sm:w-[98%]">
          {/* background image — stretches to match whatever height the content below needs */}
          <div
            className="absolute inset-0 overflow-hidden rounded-2xl bg-cover bg-center object-cover"
            style={{
              backgroundImage: `url(${
                isCreator
                  ? creatorData?.general_info?.banner_url
                  : BackdropImg
              })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          ></div>

          {/* Cinematic scrim — in normal flow, so growing content (e.g. a longer
              description) pushes this section taller instead of overflowing it */}
          <div className="relative min-h-[500px] w-full items-center videohero-back rounded-2xl bg-gradient-to-t from-black via-black/55 to-transparent pb-10 max-sm:pb-6">
            <div className="mt-10 ml-8 max-sm:ml-2">
              <BackButton />
            </div>
            <div className="ml-8 text-white mt-15 max-sm:ml-3">
              <div className="h-[100px] w-full rounded-full">
                <div className="relative h-[120px] w-[120px] overflow-hidden rounded-full ring-4 ring-white/15 shadow-2xl shadow-black/50">
                  <LazyLoadImg
                    imgSrc={creatorData?.general_info?.thumbnail_url}
                    imgAlt={"Artist"}
                    imgHeight={"100%"}
                    imgWidth={"100%"}
                    wrapperProps={{
                      style: {
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        display: "block",
                      },
                    }}
                    className="object-cover w-full h-full"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                </div>
                {!isLoading && (
                  <>
                    <div className="flex items-center mt-10 text-white text-md max-sm:mt-3">
                      {creatorData?.general_info?.type?.map(
                        (type: any, i: number) => (
                          <div className="flex mr-1" key={i}>
                            <p className="text-md font-semibold uppercase tracking-wide text-slate-300 max-sm:text-[10px]">
                              {type.name}
                              {i <
                                creatorData?.general_info?.type?.length - 1 &&
                                " | "}
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                    <div className="flex items-center mt-3 text-white text-md max-sm:mt-3 max-sm:block">
                      <div className="flex items-center">
                        <h1 className="text-4xl font-bold tracking-tight text-white drop-shadow-2xl max-sm:text-xl">
                          {creatorData?.general_info?.name}
                        </h1>
                        {isCreator ? (
                          <div className="flex items-center justify-center">
                            <img
                              src={verify}
                              alt=""
                              className="mx-3 mt-0 verify-blue-tik place-self-center max-sm:mx-2"
                              style={{ width: "25px", height: "25px" }}
                            />
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                      {isCreator && (
                        <>
                          <FollowFollowingText
                            count={creatorData?.general_info?.followers_count}
                            text="Follower"
                            likes={creatorData?.general_info?.likes}
                          />
                        </>
                      )}
                    </div>
                    <div
                      data-full-description={fullDescription}
                      className="creator-description-wrapper mt-8 w-[50%] max-sm:mt-3 max-sm:w-full max-md:w-[80%] max-lg:w-[60%]"
                    >
                      <p className="creator-description text-[14px] font-normal leading-relaxed text-white/80">
                        {fullDescription}
                      </p>
                    </div>

                    <div className="flex items-center gap-5 mt-6 max-sm:gap-3">
                      {isCreator && (
                        <div className="">
                          <SubscribeCreator
                            creatorId={creatorData?.general_info?.id}
                            user={loggedInUser}
                            creator={creatorData?.general_info}
                            setShowLoginModal={setShowLoginModal}
                          />
                        </div>
                      )}

                      {isPremiumContent ? (
                        <>
                          {isPlusUser ? (
                            <div className="flex items-center gap-1 px-10 py-4 text-sm font-semibold text-white border rounded-full border-white/15 bg-black/40 backdrop-blur-md">
                              <FaCrown className="text-[var(--red)] text-sm" />
                              <span>PREMIUM</span>
                            </div>
                          ) : (
                            <>
                              {creatorData?.general_info?.is_subscribed ? (
                                <button
                                  disabled
                                  className="flex items-center gap-2 rounded-full border border-green-500/40 bg-[#141821]/80 px-10 py-4 text-base font-bold text-white shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur-md cursor-default"
                                >
                                  <FaCheckCircle className="text-lg text-green-500" />
                                  Subscribed
                                </button>
                              ) : (
                                <>
                                  <button
                                    className="rounded-full bg-[#F50537] px-10 py-4 text-base font-bold text-white shadow-lg shadow-[#F50537]/30 transition-all duration-300 hover:scale-[1.03] hover:brightness-110 hover:shadow-xl"
                                    onClick={() =>
                                      navigate(
                                        RouteName.SubscriptionPlanCheckout,
                                        {
                                          state: {
                                            plan: packageObject,
                                          },
                                        },
                                      )
                                    }
                                  >
                                    Subscribe to Watch
                                  </button>
                                  <div className="flex items-center gap-2 px-6 py-4 text-base font-bold text-white border rounded-full shadow-lg border-white/10 bg-white/5 shadow-black/30">
                                    <FaCrown className="text-sm " />
                                    <span>
                                      $
                                      {creatorData?.general_info?.price ||
                                        "4.99"}
                                      <span className="ml-1 text-xs font-normal text-white/50">
                                        /month
                                      </span>
                                    </span>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </>
                      ) : null}
                    </div>

                    <div className="absolute right-8 top-8 max-sm:right-4 max-sm:top-4">
                      <AnimatedShareButton shareUrl={currentUrl} />
                    </div>
                  </>
                )}
                {isLoading && <CreatorHeaderSkeleton />}
              </div>
            </div>
          </div>
        </div>
        <div className="">
          <VideoCreatorButtonSection
            isLoading={isLoading}
            creatorData={creatorData}
            setIsLoading={setIsLoading}
          />
        </div>
        {showLoginModal ? (
          <LoginModel
            clickLogin={closeLoginModal}
            setShowLoginModal={setShowLoginModal}
            navigateUrl={window.location.pathname}
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
}

export default VideoCreator;
